import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import './support-inbox.css';

const TIERS = [
  { id: 'bronze', label: 'Бронза' },
  { id: 'silver', label: 'Серебро' },
  { id: 'gold', label: 'Золото' },
];
const CHANNELS = [
  { id: 'tickets', label: 'Тикеты' },
  { id: 'telegram', label: 'Telegram' },
  { id: 'max', label: 'MAX' },
];

const formatWait = (seconds) => {
  if (seconds == null) return '';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 1) return 'только что';
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} ч`;
  return `${Math.floor(hours / 24)} д`;
};

const statusLabel = (phase, status) => {
  const key = phase || status;
  if (key === 'solved') return 'Архив';
  if (key === 'awaiting_client') return 'Ждёт клиента';
  if (key === 'waiting_reply') return 'Ожидает ответа';
  if (key === 'in_progress') return 'В работе';
  return 'На рассмотрении';
};

function SupportFileModal({ file, onClose }) {
  const [src, setSrc] = React.useState('');
  const [failed, setFailed] = React.useState(false);
  const image = String(file.mime || '').startsWith('image/');

  React.useEffect(() => {
    let cancelled = false;
    let url = '';
    apiService.request('get', `/api/v1/admin/support/attachments/${file.id}`, { responseType: 'blob' })
      .then((response) => {
        if (cancelled) return;
        url = window.URL.createObjectURL(response.data);
        setSrc(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (url) window.URL.revokeObjectURL(url);
    };
  }, [file.id]);

  React.useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const download = () => {
    if (!src) return;
    const anchor = document.createElement('a');
    anchor.href = src;
    anchor.download = file.name || 'file';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  return (
    <div className="support-desk__file-modal" role="dialog" aria-modal="true" aria-label={file.name} onClick={onClose}>
      <div className="support-desk__file-dialog" onClick={(event) => event.stopPropagation()}>
        <header>
          <h3>{file.name}</h3>
          <button type="button" onClick={onClose} aria-label="Закрыть">✕</button>
        </header>
        <div className="support-desk__file-stage">
          {failed ? <p>Не удалось открыть файл</p> : null}
          {!failed && !src ? <p>Загружаем...</p> : null}
          {!failed && src && image ? <img src={src} alt={file.name} /> : null}
          {!failed && src && !image ? <p>Предпросмотр для этого типа недоступен. Файл можно скачать.</p> : null}
        </div>
        <footer>
          <button type="button" className="support-desk__send" onClick={download} disabled={!src}>Скачать</button>
          <button type="button" className="support-desk__ghost" onClick={onClose}>Закрыть</button>
        </footer>
      </div>
    </div>
  );
}

function SupportAttachment({ file }) {
  const [src, setSrc] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const image = String(file.mime || '').startsWith('image/');

  React.useEffect(() => {
    if (!image) return undefined;
    let cancelled = false;
    let url = '';
    apiService.request('get', `/api/v1/admin/support/attachments/${file.id}`, { responseType: 'blob' })
      .then((response) => {
        if (cancelled) return;
        url = window.URL.createObjectURL(response.data);
        setSrc(url);
      })
      .catch(() => setSrc(''));
    return () => {
      cancelled = true;
      if (url) window.URL.revokeObjectURL(url);
    };
  }, [file.id, image]);

  return (
    <div className="support-desk__attachment">
      {src ? (
        <button type="button" className="support-desk__attachment-preview" onClick={() => setOpen(true)}>
          <img src={src} alt={file.name} />
        </button>
      ) : null}
      <button type="button" className="support-desk__attachment-name" onClick={() => setOpen(true)}>
        {file.name}
      </button>
      {open ? <SupportFileModal file={file} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}

const replyDraftKey = (id) => `pm-support-admin-reply:${id}`;
const noteDraftKey = (id) => `pm-support-admin-note:${id}`;

const readStored = (key) => {
  try {
    return localStorage.getItem(key) || '';
  } catch {
    return '';
  }
};

const writeStored = (key, value) => {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    /* черновик не критичен */
  }
};

const REACTIONS = ['👍', '👎', '😡', '❤️', '🔥', '😂', '🙏', '✅', '👀'];

function groupedReactions(reactions) {
  const map = new Map();
  (reactions || []).forEach((item) => {
    const row = map.get(item.emoji) || { emoji: item.emoji, count: 0, mine: false };
    row.count += 1;
    row.mine = row.mine || item.mine;
    map.set(item.emoji, row);
  });
  return [...map.values()];
}

function DeskReactions({ reactions, canReact, onReact }) {
  const [open, setOpen] = React.useState(false);
  const pickerRef = React.useRef(null);
  const addRef = React.useRef(null);
  const chips = groupedReactions(reactions);
  React.useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      const target = event.target;
      if (pickerRef.current?.contains(target) || addRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  if (!chips.length && !canReact) return null;
  return (
    <div className="support-desk__react">
      {chips.map((chip) => (
        canReact ? (
          <button
            key={chip.emoji}
            type="button"
            className={`support-desk__react-chip${chip.mine ? ' is-mine' : ''}`}
            onClick={() => onReact(chip.emoji)}
          >
            {chip.emoji}{chip.count > 1 ? ` ${chip.count}` : ''}
          </button>
        ) : (
          <span key={chip.emoji} className="support-desk__react-chip">
            {chip.emoji}{chip.count > 1 ? ` ${chip.count}` : ''}
          </span>
        )
      ))}
      {canReact ? (
        <button type="button" ref={addRef} className="support-desk__react-add" aria-label="Добавить реакцию" onClick={() => setOpen((prev) => !prev)}>+</button>
      ) : null}
      {open ? (
        <div className="support-desk__react-picker" ref={pickerRef}>
          {REACTIONS.map((emoji) => (
            <button key={emoji} type="button" onClick={() => { setOpen(false); onReact(emoji); }}>{emoji}</button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SupportInbox({ isAdmin }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState('current');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(() => {
    const raw = searchParams.get('ticket');
    return raw ? Number(raw) : null;
  });
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState('');
  const [replyFiles, setReplyFiles] = useState(null);
  const [note, setNote] = useState('');
  const [composerMode, setComposerMode] = useState('reply');
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateTitle, setTemplateTitle] = useState('');
  const [templateBody, setTemplateBody] = useState('');
  const [panel, setPanel] = useState(null);
  const [narrowThread, setNarrowThread] = useState(false);

  useEffect(() => {
    if (!activeId) {
      setReply('');
      setNote('');
      return;
    }
    setReply(readStored(replyDraftKey(activeId)));
    setNote(readStored(noteDraftKey(activeId)));
  }, [activeId]);

  const loadList = useCallback(async () => {
    const response = await apiService.get('/api/v1/admin/support/tickets', { tab, q: query });
    setItems(response.data.items || []);
  }, [query, tab]);

  const loadThread = useCallback(async (ticketId) => {
    if (!ticketId) {
      setThread(null);
      return;
    }
    const response = await apiService.get(`/api/v1/admin/support/tickets/${ticketId}`);
    setThread(response.data);
  }, []);

  useEffect(() => {
    loadList().catch((err) => setError(err.response?.data?.detail || 'Не удалось загрузить тикеты'));
  }, [loadList]);

  useEffect(() => {
    if (!isAdmin) return;
    apiService.get('/api/v1/admin/support/settings')
      .then((response) => setSettings(response.data))
      .catch(() => setSettings(null));
  }, [isAdmin]);

  useEffect(() => {
    apiService.get('/api/v1/admin/support/templates')
      .then((response) => setTemplates(response.data.items || []))
      .catch(() => setTemplates([]));
  }, []);

  useEffect(() => {
    loadThread(activeId).catch((err) => setError(err.response?.data?.detail || 'Тикет не открылся'));
  }, [activeId, loadThread]);

  useEffect(() => {
    const onResize = () => setNarrowThread(window.innerWidth < 960);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const openTicket = (id) => {
    setActiveId(id);
    const next = new URLSearchParams(searchParams);
    next.set('ticket', String(id));
    setSearchParams(next, { replace: true });
  };

  const closeTicket = () => {
    setActiveId(null);
    const next = new URLSearchParams(searchParams);
    next.delete('ticket');
    setSearchParams(next, { replace: true });
  };

  const saveSettings = async (next) => {
    setSettings(next);
    const response = await apiService.put('/api/v1/admin/support/settings', next);
    setSettings(response.data);
  };

  const toggleChannel = (tier, channel) => {
    if (!settings) return;
    const matrix = { ...(settings.support_channels_by_tier || {}) };
    const current = new Set(matrix[tier] || []);
    if (current.has(channel)) current.delete(channel);
    else current.add(channel);
    const next = { ...settings, support_channels_by_tier: { ...matrix, [tier]: [...current] } };
    saveSettings(next).catch((err) => setError(err.response?.data?.detail || 'Не сохранилось'));
  };

  const sendReply = async () => {
    if (!activeId || reply.trim().length < 1) return;
    const response = await apiService.post(`/api/v1/admin/support/tickets/${activeId}/messages`, { body: reply });
    if (replyFiles && replyFiles.length > 0 && response.data.message_id) {
      const form = new FormData();
      Array.from(replyFiles).forEach((file) => form.append('files', file));
      await apiService.request('post', `/api/v1/admin/support/messages/${response.data.message_id}/attachments`, {
        data: form,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    setReply('');
    writeStored(replyDraftKey(activeId), '');
    setReplyFiles(null);
    await loadThread(activeId);
    await loadList();
  };

  const sendNote = async () => {
    if (!activeId || note.trim().length < 1) return;
    await apiService.post(`/api/v1/admin/support/tickets/${activeId}/notes`, { body: note });
    setNote('');
    writeStored(noteDraftKey(activeId), '');
    await loadThread(activeId);
  };

  const reactToMessage = async (messageId, emoji) => {
    await apiService.request('patch', `/api/v1/admin/support/messages/${messageId}/reaction`, { data: { value: emoji } });
    if (activeId != null) await loadThread(activeId);
  };

  const submitComposer = () => {
    if (composerMode === 'note') {
      sendNote().catch((err) => setError(err.response?.data?.detail || 'Заметка не сохранилась'));
      return;
    }
    sendReply().catch((err) => setError(err.response?.data?.detail || 'Ответ не отправился'));
  };

  const changeStatus = async (status) => {
    await apiService.post(`/api/v1/admin/support/tickets/${activeId}/status`, { status });
    await loadThread(activeId);
    await loadList();
  };

  const takeTicket = async () => {
    await apiService.post(`/api/v1/admin/support/tickets/${activeId}/assign`, {});
    await loadThread(activeId);
    await loadList();
  };

  const addTemplate = async () => {
    const response = await apiService.post('/api/v1/admin/support/templates', {
      title: templateTitle,
      body: templateBody,
    });
    setTemplates((prev) => [response.data, ...prev]);
    setTemplateTitle('');
    setTemplateBody('');
  };

  const removeTemplate = async (templateId) => {
    await apiService.delete(`/api/v1/admin/support/templates/${templateId}`);
    setTemplates((prev) => prev.filter((item) => item.id !== templateId));
  };

  const ticket = thread?.ticket;
  const showList = !narrowThread || !activeId;
  const showThread = !narrowThread || Boolean(activeId);
  const matrixDisabled = settings && settings.support_tickets_enabled === false;
  const proxyChips = useMemo(() => ticket?.proxies || [], [ticket]);
  const updateReply = (value) => {
    setReply(value);
    if (activeId) writeStored(replyDraftKey(activeId), value);
  };
  const updateNote = (value) => {
    setNote(value);
    if (activeId) writeStored(noteDraftKey(activeId), value);
  };
  const composerValue = composerMode === 'note' ? note : reply;
  const setComposerValue = composerMode === 'note' ? updateNote : updateReply;

  return (
    <div className="support-desk">
      <header className="support-desk__bar">
        <div>
          <h1>Поддержка</h1>
          <p>{tab === 'archive' ? 'Архив обращений' : 'Очередь, которая ждёт ответа'}</p>
        </div>
        <div className="support-desk__bar-actions">
          {isAdmin ? (
            <button type="button" className="support-desk__ghost" onClick={() => setPanel('settings')}>Каналы</button>
          ) : null}
          <button type="button" className="support-desk__ghost" onClick={() => setPanel('templates')}>Шаблоны</button>
        </div>
      </header>
      {error ? <p className="support-desk__error">{String(error)}</p> : null}
      <div className="support-desk__shell">
        {showList ? (
          <aside className="support-desk__list">
            <div className="support-desk__tabs" role="tablist">
              <button type="button" className={tab === 'current' ? 'is-active' : ''} onClick={() => setTab('current')}>Актуальные</button>
              <button type="button" className={tab === 'archive' ? 'is-active' : ''} onClick={() => setTab('archive')}>Архив</button>
            </div>
            <input
              className="support-desk__search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Поиск: тема, клиент, порт, IP"
              aria-label="Поиск тикетов"
            />
            <div className="support-desk__rows">
              {items.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  className={`support-desk__row${item.id === activeId ? ' is-active' : ''}`}
                  onClick={() => openTicket(item.id)}
                >
                  <span className="support-desk__row-top">
                    <strong>{item.subject}</strong>
                    {item.staff_unread_count > 0 ? <em>{item.staff_unread_count}</em> : null}
                  </span>
                  <span className="support-desk__row-meta">#{item.id} · {item.client_login}</span>
                  <span className="support-desk__row-meta">
                    <span className={`support-desk__pill support-desk__pill--${item.phase || item.status}`}>{statusLabel(item.phase, item.status)}</span>
                    {item.waiting_seconds != null ? ` · ${formatWait(item.waiting_seconds)}` : ''}
                  </span>
                </button>
              ))}
              {items.length === 0 ? <p className="support-desk__empty">В этой вкладке пусто</p> : null}
            </div>
          </aside>
        ) : null}
        {showThread ? (
          <section className="support-desk__thread">
            {!ticket ? (
              <div className="support-desk__placeholder">
                <h2>Выберите обращение</h2>
                <p>Слева очередь. Открытый тикет останется здесь, настройки и шаблоны не занимают это место.</p>
              </div>
            ) : (
              <>
                <header className="support-desk__thread-head">
                  {narrowThread ? <button type="button" className="support-desk__ghost" onClick={closeTicket}>Назад</button> : null}
                  <div className="support-desk__thread-title">
                    <h2>{ticket.subject}</h2>
                    <p>{ticket.client_login}{ticket.client_email ? ` · ${ticket.client_email}` : ''}</p>
                  </div>
                  <span className={`support-desk__pill support-desk__pill--${ticket.phase || ticket.status}`}>{statusLabel(ticket.phase, ticket.status)}</span>
                </header>
                <div className="support-desk__context">
                  <a href={ticket.client_id ? `/clients?client_id=${ticket.client_id}` : `/clients?client_login=${encodeURIComponent(ticket.client_login || '')}`}>Клиент</a>
                  {proxyChips.map((proxy) => (
                    <a key={proxy.id} href={`/proxies?proxy_id=${proxy.id}`}>{proxy.label}</a>
                  ))}
                  <button type="button" onClick={takeTicket}>{ticket.assignee_name ? `Взял ${ticket.assignee_name}` : 'Взять в работу'}</button>
                  <button type="button" onClick={() => changeStatus('open')}>На рассмотрении</button>
                  <button type="button" onClick={() => changeStatus('awaiting_client')}>Ждёт клиента</button>
                  <button type="button" onClick={() => changeStatus('solved')}>В архив</button>
                </div>
                <div className="support-desk__messages">
                  {(thread.messages || []).map((message) => (
                    <article
                      key={message.id}
                      className={`support-desk__bubble support-desk__bubble--${message.audience === 'internal' ? 'note' : message.author_role}`}
                    >
                      <header>
                        {message.audience === 'internal' ? 'Заметка' : message.author_role === 'support' ? 'Поддержка' : 'Клиент'}
                        {message.author_name ? ` · ${message.author_name}` : ''}
                      </header>
                      <p>{message.body}</p>
                      {(message.attachments || []).length ? (
                        <div className="support-desk__attachments">
                          {message.attachments.map((file) => <SupportAttachment key={file.id} file={file} />)}
                        </div>
                      ) : null}
                      <DeskReactions
                        reactions={message.reactions}
                        canReact={message.author_role === 'client' && message.audience !== 'internal'}
                        onReact={(emoji) => reactToMessage(message.id, emoji)}
                      />
                    </article>
                  ))}
                </div>
                <form
                  className="support-desk__composer"
                  onSubmit={(event) => {
                    event.preventDefault();
                    submitComposer();
                  }}
                >
                  <div className="support-desk__composer-modes">
                    <button type="button" className={composerMode === 'reply' ? 'is-active' : ''} onClick={() => setComposerMode('reply')}>Ответ клиенту</button>
                    <button type="button" className={composerMode === 'note' ? 'is-active' : ''} onClick={() => setComposerMode('note')}>Заметка</button>
                    {composerMode === 'reply' ? (
                      <select
                        aria-label="Подставить шаблон"
                        value=""
                        onChange={(event) => {
                          const found = templates.find((item) => String(item.id) === event.target.value);
                          if (found) updateReply(found.body);
                        }}
                      >
                        <option value="">Шаблон</option>
                        {templates.map((item) => (
                          <option key={item.id} value={item.id}>{item.title}</option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                  <textarea
                    value={composerValue}
                    onChange={(event) => setComposerValue(event.target.value)}
                    rows={3}
                    placeholder={composerMode === 'note' ? 'Видно только сотрудникам' : 'Ответ клиенту'}
                  />
                  <div className="support-desk__composer-bar">
                    {composerMode === 'reply' ? (
                      <div className="support-desk__files">
                        <input id="support-desk-files" type="file" multiple accept="image/*,.pdf,.doc,.docx,.txt" onChange={(event) => setReplyFiles(event.target.files)} />
                        <label htmlFor="support-desk-files" className="support-desk__file-btn">Прикрепить файлы</label>
                        <span className="support-desk__file-names">
                          {replyFiles && replyFiles.length > 0 ? Array.from(replyFiles).map((file) => file.name).join(', ') : 'Ничего не выбрано'}
                        </span>
                      </div>
                    ) : <span />}
                    <button type="submit" className="support-desk__send">{composerMode === 'note' ? 'Сохранить заметку' : 'Отправить'}</button>
                  </div>
                </form>
              </>
            )}
          </section>
        ) : null}
      </div>
      {panel ? (
        <div className="support-desk__overlay" onClick={() => setPanel(null)}>
          <aside className="support-desk__drawer" onClick={(event) => event.stopPropagation()} aria-label={panel === 'settings' ? 'Каналы' : 'Шаблоны'}>
            <header>
              <h2>{panel === 'settings' ? 'Каналы и нагрузка' : 'Шаблоны ответов'}</h2>
              <button type="button" onClick={() => setPanel(null)} aria-label="Закрыть">✕</button>
            </header>
            {panel === 'settings' && settings ? (
              <div className="support-desk__drawer-body">
                <label className="support-inbox__switch">
                  <input
                    type="checkbox"
                    checked={Boolean(settings.support_tickets_enabled)}
                    onChange={(event) => saveSettings({ ...settings, support_tickets_enabled: event.target.checked })}
                  />
                  Тикеты включены
                </label>
                <label className="support-inbox__switch">
                  <input
                    type="checkbox"
                    checked={settings.support_admin_telegram_enabled !== false}
                    onChange={(event) => saveSettings({ ...settings, support_admin_telegram_enabled: event.target.checked })}
                  />
                  Уведомления админу в Telegram
                </label>
                <label className="support-inbox__switch">
                  <input
                    type="checkbox"
                    checked={Boolean(settings.support_high_load_enabled)}
                    onChange={(event) => saveSettings({ ...settings, support_high_load_enabled: event.target.checked })}
                  />
                  Повышенная нагрузка
                </label>
                <label className="support-inbox__minutes">
                  Минуты в плашке
                  <input
                    type="number"
                    min="1"
                    value={settings.support_high_load_minutes || ''}
                    onChange={(event) => {
                      const value = event.target.value === '' ? null : Number(event.target.value);
                      saveSettings({ ...settings, support_high_load_minutes: value });
                    }}
                  />
                </label>
                <label className="support-inbox__load-text">
                  Текст плашки для клиентов
                  <textarea
                    rows={3}
                    maxLength={500}
                    value={settings.support_high_load_text || ''}
                    onChange={(event) => setSettings({ ...settings, support_high_load_text: event.target.value })}
                    onBlur={(event) => saveSettings({ ...settings, support_high_load_text: event.target.value })}
                  />
                </label>
                <table className="support-inbox__matrix">
                  <thead>
                    <tr>
                      <th>Уровень</th>
                      {CHANNELS.map((channel) => <th key={channel.id}>{channel.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {TIERS.map((tier) => (
                      <tr key={tier.id}>
                        <td>{tier.label}</td>
                        {CHANNELS.map((channel) => {
                          const checked = (settings.support_channels_by_tier?.[tier.id] || []).includes(channel.id);
                          return (
                            <td key={channel.id}>
                              <input
                                type="checkbox"
                                aria-label={`${tier.label} ${channel.label}`}
                                checked={checked}
                                disabled={channel.id === 'tickets' && matrixDisabled}
                                onChange={() => toggleChannel(tier.id, channel.id)}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            {panel === 'templates' ? (
              <div className="support-desk__drawer-body">
                {isAdmin ? (
                  <div className="support-inbox__template-form">
                    <input value={templateTitle} onChange={(event) => setTemplateTitle(event.target.value)} placeholder="Название" aria-label="Название шаблона" />
                    <textarea value={templateBody} onChange={(event) => setTemplateBody(event.target.value)} placeholder="Текст ответа" aria-label="Текст шаблона" rows={4} />
                    <button type="button" className="support-desk__send" onClick={addTemplate}>Добавить</button>
                  </div>
                ) : null}
                <ul className="support-inbox__template-list">
                  {templates.map((item) => (
                    <li key={item.id}>
                      <div>
                        <strong>{item.title}</strong>
                        <p>{item.body}</p>
                      </div>
                      {isAdmin ? <button type="button" className="support-desk__ghost" onClick={() => removeTemplate(item.id)}>Удалить</button> : null}
                    </li>
                  ))}
                </ul>
                {templates.length === 0 ? <p className="support-desk__empty">Шаблонов пока нет</p> : null}
              </div>
            ) : null}
          </aside>
        </div>
      ) : null}
    </div>
  );
}

export default SupportInbox;
